/**
 * Générateur — Connaître les carrés des entiers de 0 à 12.
 * Attendu ID : 927 | Niveau : 5ème | Domaine : Puissances (cluster 2, O29)
 *
 * Plan de cartes (validé 2026-07-11) :
 *
 *   Carte « produit » — Ex.4 Écrire le produit et calculer
 *     texte_trou_saisie : « 6² = {{6}} × {{6}} = {{36}} ; 9² = … ». DEUX carrés
 *     distincts (a, c ∈ 2..12) par tirage. Le cube est HORS PROGRAMME 5e : la
 *     notation a³ a été retirée de tout le générateur (attendu = carrés seuls).
 *
 *   Carte « table » — Créé n°18 La table des carrés
 *     relier (5 paires nombre ↔ carré, bases 0..12 distinctes → carrés
 *     distincts d'office ; contrainte anti-confusion : jamais a et a² tous
 *     deux PRÉSENTS comme bases du même jeu, ex. 3 et 9) + texte_trou_saisie
 *     (la variante du doc : 3 carrés à compléter).
 *
 *   Carte « retrouver » — Créé n°19 Retrouver le nombre (sens inverse,
 *     prépare O31) : texte (saisie, unicité = racine entière positive
 *     unique) + qcm_unique (distracteurs : voisins de table a−1/a+1, moitié
 *     c/2 quand elle est entière). Deux gabarits d'énoncé (question directe /
 *     énigme « Qui suis-je ? » du cahier) pour la variabilité.
 *
 *   Carte « double/carré/triple » — Ex.12 (cube RETIRÉ : hors programme 5e).
 *     texte_trou_saisie (2 nombres distincts par tirage, a ∈ 3..12 — a = 2
 *     exclu car double = carré) + tableau bijection §4bis (a ∈ 4..12 : les 3
 *     valeurs 2a, a², 3a sont distinctes par construction — a = 3 exclu car
 *     3a = a²). En-têtes verbalisés (« Le double de 5 »), pas de littéral.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, makeRelier, basePayload,
} from '../../../utils';

const ATTENDU_ID = 927;

const L_PRODUIT = 'Écrire le produit et calculer';
const L_TABLE = 'La table des carrés';
const L_RETROUVER = 'Retrouver le nombre';
const L_DCT = 'Double, carré, triple';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ Carte 1 — Écrire le produit et calculer (Ex.4) ═════════

const genProduitTrous = () => {
    // deux carrés distincts (table 0-12 ; 0 et 1 triviaux exclus)
    const a = randInt(2, 12);
    let c = randInt(2, 12);
    if (c === a) c = a === 12 ? 11 : c + 1;
    // Une égalité par ligne (convention 2026-09-02 : jamais « ; » entre deux
    // égalités à trous, l'impression coupait la ligne au milieu).
    const phrase = `${a}² = {{${a}}} × {{${a}}} = {{${a * a}}}\n`
        + `${c}² = {{${c}}} × {{${c}}} = {{${c * c}}}`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Écris chaque nombre sous forme de produit, puis calcule.</b> '
        + '(Exemple : 4² = 4 × 4 = 16.)</p>',
        { ...opts(L_PRODUIT), mode: 'saisie' });
    payload._meta.params = { a, c };
    return payload;
};

// ═════════ Carte 2 — La table des carrés (Créé n°18) ═════════

/**
 * n bases distinctes dans 0..12 sans couple (a, a²) — la présence simultanée
 * de 3 et 9 (ou 2 et 4) comme BASES rend la confusion base/carré invisible.
 */
const tirerBasesSansCarreInterne = (n) => {
    for (let essai = 0; essai < 40; essai++) {
        const bases = shuffle([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).slice(0, n);
        const set = new Set(bases);
        if (!bases.some(x => x !== x * x && set.has(x * x))) return bases;
    }
    return [5, 6, 7, 11, 12].slice(0, n);   // repli vérifié : aucun carré interne
};

const genTableRelier = () => {
    const bases = tirerBasesSansCarreInterne(5);
    const pairs = bases.map(x => ({ source: String(x), target: String(x * x) }));
    const payload = makeRelier(pairs,
        '<p><b>Relie chaque nombre à son carré.</b></p>',
        opts(L_TABLE));
    payload._meta.params = { bases };
    return payload;
};

const genTableTrous = () => {
    const bases = shuffle([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).slice(0, 3);
    const phrase = bases.map(x => `${x}² = {{${x * x}}}`).join('\n'); // une égalité par ligne
    const payload = makeTexteTrou(phrase,
        '<p><b>Donne le carré de chaque nombre.</b></p>',
        { ...opts(L_TABLE), mode: 'saisie' });
    payload._meta.params = { bases };
    return payload;
};

// ═════════ Carte 3 — Retrouver le nombre (Créé n°19) ═════════

const enonceRetrouver = (carre, gabarit) => (gabarit === 'question'
    ? `<p><b>Quel nombre entier positif a pour carré ${carre} ?</b></p>`
    : `<p><b>Je suis un nombre entier positif. Mon carré vaut ${carre}. Qui suis-je ?</b></p>`);

const genRetrouverSaisie = () => {
    const a = randInt(0, 12);
    const gabarit = randomChoice(['question', 'enigme']);
    const payload = basePayload('texte', enonceRetrouver(a * a, gabarit), {
        bonne_reponse: String(a), strict: false, suffixe: '',
    }, opts(L_RETROUVER));
    payload._meta.params = { a, gabarit };
    return payload;
};

const genRetrouverQcm = () => {
    const a = randInt(2, 12);
    const c = a * a;
    const gabarit = randomChoice(['question', 'enigme']);
    const candidats = [
        String(a - 1),                                       // voisin de table
        String(a + 1),                                       // voisin de table
        (c % 2 === 0 && c / 2 !== a) ? String(c / 2) : String(a + 2),   // la moitié du carré
    ];
    const distincts = [...new Set(candidats)].filter(x => x !== String(a));
    const payload = makeQcm([String(a), ...distincts], String(a),
        enonceRetrouver(c, gabarit), opts(L_RETROUVER));
    payload._meta.params = { a, gabarit };
    return payload;
};

// ═════════ Carte 4 — Double, carré, triple (Ex.12) ═════════
// Cube RETIRÉ : la notation a³ est hors programme de 5e (attendu = carrés).

const OPERATIONS_DCT = [
    { nom: 'double', calc: (x) => 2 * x },
    { nom: 'carré', calc: (x) => x * x },
    { nom: 'triple', calc: (x) => 3 * x },
];

const genDctTrous = () => {
    // a ≥ 3 (a = 2 : double = carré = 4) ; deux nombres distincts pour la variété
    const a1 = randInt(3, 12);
    let a2 = randInt(3, 12);
    if (a2 === a1) a2 = a1 === 12 ? 11 : a1 + 1;
    const ligne = (x) => `Nombre : ${x} → double : {{${2 * x}}} ; carré : {{${x * x}}} ; `
        + `triple : {{${3 * x}}}`;
    // Un nombre par ligne ; le « ; » ne sépare que les trois attributs d'un même nombre.
    const phrase = `${ligne(a1)}\n${ligne(a2)}`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Pour chaque nombre, donne son double, son carré et son triple.</b></p>',
        { ...opts(L_DCT), mode: 'saisie' });
    payload._meta.params = { a1, a2 };
    return payload;
};

const genDctBijection = () => {
    // a ≥ 4 : 2a, a², 3a deux à deux distincts (a = 2 : double = carré ; a = 3 : triple = carré)
    const a = randomChoice([4, 5, 6, 7, 8, 9, 10, 11, 12]);
    const operations = shuffle(OPERATIONS_DCT); // les 3 (min 3 propositions) ; l'ordre varie
    const lignes = operations.map(op => `Le ${op.nom} de ${a}`);
    const valeurs = operations.map(op => String(op.calc(a)));
    const colonnes = shuffle([...valeurs]);
    const bonnesReponses = valeurs.map(v => colonnes.indexOf(v));
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnesReponses[i]]}]`).join('');
    const tableElement = {
        type: 'table',
        config: { type: 'association', colonnes, lignes, bonnesReponses },
    };
    const payload = basePayload('qcm',
        '<p><b>Associe chaque expression à sa valeur.</b> Chaque colonne ne sert qu\'une fois.</p>',
        { choix: [], bonne_reponse: br },
        opts(L_DCT),
        { version: 1, elements: [tableElement] });
    payload._meta.params = { a, operations: operations.map(o => o.nom) };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    produit_texte_trou_saisie: genProduitTrous,
    table_relier: genTableRelier,
    table_texte_trou_saisie: genTableTrous,
    retrouver_texte: genRetrouverSaisie,
    retrouver_qcm_unique: genRetrouverQcm,
    dct_texte_trou_saisie: genDctTrous,
    dct_tableau_bijection: genDctBijection,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', relier: 'Relier', texte: 'Saisie',
    qcm_unique: 'QCM', tableau_bijection: 'Tableau',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'produit', label: L_PRODUIT, formatsList: buildFormats('produit') },
    { id: 'table', label: L_TABLE, formatsList: buildFormats('table') },
    { id: 'retrouver', label: L_RETROUVER, formatsList: buildFormats('retrouver') },
    { id: 'dct', label: L_DCT, formatsList: buildFormats('dct') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
